import React, { useMemo } from "react"
import { Box } from "@chakra-ui/react"
import { Combobox } from "components/Combobox"
import { Filter, SegmentRuleOperator } from "sharedTypes"
import { FilterFieldConfig, FilterOperatorOption } from "./types"
import { ChipSize, CHIP_SIZES } from "./FilterChip"
import { operatorLabel } from "./operatorLogic"

type Props = {
  filter: Filter
  config: FilterFieldConfig
  size?: ChipSize
  onChange: (operator: SegmentRuleOperator) => void
}

// Same trigger styling as the value selector so the two cells read as one
// continuous chip body. Combobox uses Portal for its popover content, so the
// dropdown clears any z-index conflict with the sticky table header below.
const triggerProps = (size: ChipSize) => {
  const tokens = CHIP_SIZES[size]

  return {
    px: tokens.px,
    h: "100%",
    display: "flex",
    alignItems: "center",
    fontSize: tokens.fontSize,
    cursor: "pointer",
    color: "gray.700",
    _hover: { bg: "gray.50" },
  } as const
}

const FilterChipOperator = ({
  filter, config, size = "md", onChange,
}: Props) => {
  // Dropdown contents change with the field type AND the current value
  // count:
  // - multiValuePerGuest field with 2+ values → 4 options (any/all × in/out).
  // - any other multiSelect field with 2+ values → 2 array ops.
  // - 0-1 values → fall back to config.operators (scalar ops), since
  //   "any of [X]" and "all of [X]" are mathematically identical.
  const items: FilterOperatorOption[] = useMemo(() => {
    if (config.multiValuePerGuest && filter.values.length >= 2) {
      return [
        { value: "in", label: "include any of" },
        { value: "has_all", label: "include all of" },
        { value: "not_in", label: "exclude if any of" },
        { value: "not_has_all", label: "exclude if all of" },
      ]
    }
    if (config.multiSelect && filter.values.length >= 2) {
      return [
        { value: "in", label: "is any of" },
        { value: "not_in", label: "is not" },
      ]
    }
    // multiValuePerGuest field, 0–1 values: drop "is"/"is not" in favour
    // of the set-membership verbs that match the 4-op vocabulary above.
    if (config.multiValuePerGuest) {
      return [
        { value: "eq", label: "include" },
        { value: "ne", label: "do not include" },
      ]
    }
    // multiSelect field, 0–1 values: "is any of [X]" is identical to "is X"
    // at this count, so only offer the scalar pair — config.operators can
    // still carry "is any of"/"is not any of" for fields like Company/Job
    // Title (MULTI_ENUM_OPERATORS), which would otherwise let you pick an
    // operator here that normalizeOperator immediately reverts on the chip.
    if (config.multiSelect) {
      return [
        { value: "eq", label: "is" },
        { value: "ne", label: "is not" },
      ]
    }

    return config.operators
  }, [config, filter.values.length])

  const selected = items.find((op) => op.value === filter.operator) ?? null
  const triggerLabel = operatorLabel(filter.operator, filter.values.length, config)

  return (
    <Combobox.Root
      items={items}
      getItemKey={(op) => op.value}
      getItemLabel={(op) => op.label}
      value={selected}
      onChange={(op) => { if (op) onChange(op.value) }}
    >
      <Combobox.Trigger>
        <Box {...triggerProps(size)} data-testid="filter-chip-operator">
          {triggerLabel}
        </Box>
      </Combobox.Trigger>
      <Combobox.Content w="180px" arrowBg="white">
        <Combobox.List<FilterOperatorOption> maxH="240px">
          {(item) => (
            <Combobox.Item item={item}>{item.label}</Combobox.Item>
          )}
        </Combobox.List>
      </Combobox.Content>
    </Combobox.Root>
  )
}

export default FilterChipOperator
